import { t } from "@lingui/macro";
import { FlashList, ListRenderItemInfo } from "@shopify/flash-list";
import graphql from "babel-plugin-relay/macro";
import React, {
  ComponentType,
  ReactElement,
  JSXElementConstructor,
  useMemo,
  useCallback,
} from "react";
import { Platform } from "react-native";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { SearchIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { Patron } from "../../types";
import { PatronsListItem } from "../PatronsListItem";
import { GuestListQuery } from "./__generated__/GuestListQuery.graphql";

interface iGuestListProps {
  onCheckPlayer: (player: Patron, checked: boolean) => void;
  onRemovePlayer?: (playerId: string) => void;
  checkNewDisabled?: boolean;
  ListHeaderComponent?:
    | ComponentType<any>
    | ReactElement<any, string | JSXElementConstructor<any>>
    | null;
  ListFooterComponent?:
    | ComponentType<any>
    | ReactElement<any, string | JSXElementConstructor<any>>
    | null;
  bg: React.ComponentProps<typeof Box>["bg"];
  players?: string[];
  allPlayers?: string[];
  currentPatron?: Patron;
  guestQueryRef: PreloadedQuery<GuestListQuery>;
  keepItemInListAfterCheck?: boolean;
  selectionAction?: "swap" | "replace";
  disabledPlayerIds?: string[];
}

export const GuestList = ({
  onCheckPlayer,
  onRemovePlayer,
  checkNewDisabled,
  ListFooterComponent,
  ListHeaderComponent,
  bg,
  players,
  allPlayers,
  currentPatron,
  guestQueryRef,
  keepItemInListAfterCheck = false,
  selectionAction,
  disabledPlayerIds = [],
}: iGuestListProps) => {
  const queryData = usePreloadedQuery<GuestListQuery>(
    GuestListQueryString,
    guestQueryRef
  );

  const guestData =
    queryData.session?.currentClientProfile?.member?.club?.guests;

  const hostId = useMemo(() => {
    const isHostInPlayerList = players?.some(
      (player) => player === currentPatron?.playerId
    );

    return isHostInPlayerList
      ? currentPatron?.playerId
      : players?.length
      ? players[0]
      : undefined;
  }, [currentPatron?.playerId, players]);
  const shouldRemovePlayers = !keepItemInListAfterCheck;

  const isItemSelectedAndKept = useCallback(
    (id?: string | null) =>
      keepItemInListAfterCheck && (players?.includes(id ?? "") ?? false),
    [keepItemInListAfterCheck, players]
  );

  let mappedPlayers: Patron[] =
    guestData?.edges
      ?.filter((g) =>
        shouldRemovePlayers ? !allPlayers?.includes(g?.node?.id ?? "") : true
      )
      ?.map((guest) => ({
        playerId: guest?.node?.id || "",
        firstName: guest?.node?.firstName || "",
        lastName: guest?.node?.lastName || "",
        profileImageId: guest?.node?.profileImage?.publicId || "",
        transportationType:
          guest?.node?.preferredTransportationType ?? undefined,
        type: "Guest",
        hostId,
        memberGroups: [],
        paymentStatus: "AUTO_CHARGE",
      })) || [];

  // If we are keeping items after check and players array is present, sort so guests in `players` come first.
  if (keepItemInListAfterCheck && players?.length && players.length > 0) {
    mappedPlayers = mappedPlayers.slice().sort((a, b) => {
      const aChecked = players.includes(a.playerId ?? "");
      const bChecked = players.includes(b.playerId ?? "");
      if (aChecked === bChecked) return 0;
      return aChecked ? -1 : 1;
    });

    // Remove duplicates by playerId from mappedPlayers
    if (mappedPlayers?.length) {
      const seen = new Set<string>();
      mappedPlayers = mappedPlayers.filter((player) => {
        if (!player.playerId) return false;
        if (seen.has(player.playerId)) return false;
        seen.add(player.playerId);
        return true;
      });
    }
  }

  return (
    <Box bg={bg} flex={1}>
      <FlashList
        data={mappedPlayers}
        showsVerticalScrollIndicator={Platform.OS === "web"}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        ListHeaderComponent={ListHeaderComponent}
        keyExtractor={(item) => `guest-item-${item.playerId}`}
        renderItem={({ item }: ListRenderItemInfo<Patron>) => {
          return (
            <PatronsListItem
              key={`guest-item-${item.playerId}`}
              item={item}
              handleCheck={onCheckPlayer}
              onRemovePlayer={onRemovePlayer}
              checkNewDisabled={
                disabledPlayerIds.includes(item.playerId ?? "") ||
                (isItemSelectedAndKept(item.playerId)
                  ? false
                  : checkNewDisabled)
              }
              initiallyChecked={
                isItemSelectedAndKept(item.playerId) ? true : false
              }
              keepItemInListAfterCheck={keepItemInListAfterCheck}
              selectionAction={selectionAction}
            />
          );
        }}
        ListEmptyComponent={
          <Box px={6} py={4}>
            <VStack
              justifyContent="center"
              alignItems="center"
              bg="grayscale-100"
              flex={1}
              py={4}
              gap={4}
            >
              <SearchIcon color="grayscale.800" />
              <Text color="text-secondary" variant="mainBaseMedium">
                {t`No guests match that search.`}
              </Text>
            </VStack>
          </Box>
        }
        ListFooterComponent={ListFooterComponent}
      />
    </Box>
  );
};

export const GuestListQueryString = graphql`
  query GuestListQuery($search: String, $players: [ID], $slotId: ID!) {
    session {
      currentClientProfile {
        member {
          club {
            guests(first: 12, search: $search, players: $players) {
              edges {
                node {
                  id
                  firstName
                  lastName
                  profileImage {
                    # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
                    publicId: renderingKey
                  }
                  preferredTransportationType(slotId: $slotId) {
                    id
                    code
                    customCode
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
